import { getUploadDocumentMessage } from '@backrs_ui/grow-ui/src/components/Lubav/fileUpload.util';
import {
  DocumentType,
  OfficeHoursSessionType,
  LubavSupersession,
  OFFICE_HOURS_SESSION_TYPES,
  LUBAV_SUPERSESSIONS,
  SENDER_TYPE,
  OfficeHoursMessage,
} from '@backrs_ui/grow-ui/src/models/models';
import {
  addOfficeHoursMessage,
  setIsLoading,
  confirmCoachingUserDocument,
  setPendingMessageState,
} from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  createSessionAction,
  getActiveLubavSession,
  getSessionState,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import {
  selectUserDocuments,
  selectUserId,
  confirmDocument,
} from '@backrs_ui/grow-ui/src/redux/user/user';

const SESSION_REQUIREMENTS: Record<
  OfficeHoursSessionType | LubavSupersession,
  DocumentType[] | undefined
> = {
  [OFFICE_HOURS_SESSION_TYPES.BOOST_YOUR_PROFILE]: ['resume'],
  [OFFICE_HOURS_SESSION_TYPES.BOOST_ODDS_OF_AN_OFFER]: ['resume'],
  [OFFICE_HOURS_SESSION_TYPES.RESUME_OPTIMIZER]: ['resume'],
  [OFFICE_HOURS_SESSION_TYPES.ESSAY_IDEAS]: undefined,
  [OFFICE_HOURS_SESSION_TYPES.WHAT_ARE_YOUR_CHANCES]: ['resume'],
  [OFFICE_HOURS_SESSION_TYPES.THE_INTERPRETER]: ['personal_assessment'],
  [LUBAV_SUPERSESSIONS.SUPERCOACH]: undefined,
  [LUBAV_SUPERSESSIONS.SUPERUSER]: undefined,
};

export const useDocumentHandler = () => {
  const dispatch = useAppDispatch();
  const userId = useAppSelector(selectUserId);

  const activeSession = useAppSelector(getActiveLubavSession);

  const { coachingUser, pendingMessageState } = useAppSelector(
    (state) => getSessionState(state, activeSession).coachLubav,
  );
  const userDocuments = useAppSelector(selectUserDocuments);

  const documents = coachingUser ? coachingUser.documents : userDocuments;

  const getRequiredDocuments = (): DocumentType[] | undefined => {
    if (!activeSession) return undefined;

    return SESSION_REQUIREMENTS[activeSession as OfficeHoursSessionType | LubavSupersession];
  };

  const shouldRenderFileUpload = (
    messageIndex: number,
    allMessages: OfficeHoursMessage[],
    documentType: DocumentType,
  ): boolean => {
    if (!documents) return false;

    if (!pendingMessageState || documents[documentType].confirmed) {
      return false;
    }

    // this finds the last index with a FILE_UPLOAD component
    const lastFileUploadIndex = allMessages.reduce(
      (acc, msg, i) => (msg.componentType?.type === 'FILE_UPLOAD' ? i : acc),
      -1,
    );

    return lastFileUploadIndex === messageIndex;
  };

  const promptForDocument = async (documentType: DocumentType, allowSkipDocument?: boolean) => {
    if (!activeSession || !userId || !documents) return;

    const document = documents[documentType];
    const hasDocument = document.filename !== null;

    try {
      dispatch(
        createSessionAction(
          addOfficeHoursMessage,
          activeSession,
        )({
          text: getUploadDocumentMessage({
            documentType,
            existingFileName: document.filename ?? undefined,
            existingFileUrl: document.url ?? undefined,
            coachingUser: coachingUser ? coachingUser : undefined,
          }),
          sender: SENDER_TYPE.AI,
          componentType: hasDocument
            ? { type: 'CONFIRM_DOCUMENT' as const, documentType }
            : { type: 'FILE_UPLOAD' as const, documentType, allowSkipDocument: allowSkipDocument },
        }),
      );

      dispatch(createSessionAction(setIsLoading, activeSession)(false));
    } catch (error) {
      console.error('Error prompting for document:', error);
    }
  };

  const checkDocumentRequirements = async (
    message: string,
    promptRequiredDocs?: DocumentType[],
    justConfirmedDocs?: DocumentType[],
    allowSkipDocument?: boolean,
  ): Promise<{ hasRequiredDocuments: boolean; missingDocument?: DocumentType }> => {
    // required documents may come from the selected supercoach prompt, or from the session requirements
    const requiredDocs = promptRequiredDocs || getRequiredDocuments();

    if (!requiredDocs || !documents) return { hasRequiredDocuments: true };

    for (const docType of requiredDocs) {
      const isConfirmed =
        documents[docType].confirmed || (justConfirmedDocs?.includes(docType) ?? false);

      if (!isConfirmed) {
        dispatch(
          createSessionAction(setPendingMessageState, activeSession)({
            message,
            requiredDocuments: requiredDocs,
            allowSkipDocument,
          }),
        );
        await promptForDocument(docType, allowSkipDocument);

        return { hasRequiredDocuments: false, missingDocument: docType };
      }
    }

    return { hasRequiredDocuments: true };
  };

  const handleDocumentConfirmation = (documentType: DocumentType) => {
    if (coachingUser) {
      // For supercoach sessions, update the student's documents in the session state
      dispatch(createSessionAction(confirmCoachingUserDocument, activeSession)({ documentType }));
    } else {
      // For non-supercoach sessions, update the authenticated user documents
      dispatch(confirmDocument({ documentType }));
    }

    const pendingInfo = {
      message: pendingMessageState?.message || null,
      originalRequiredDocuments: pendingMessageState?.requiredDocuments || null,
      originalAllowSkipDocument: pendingMessageState?.allowSkipDocument || false,
    };

    dispatch(createSessionAction(setPendingMessageState, activeSession)(null));

    return pendingInfo;
  };

  return {
    pendingMessageState,
    checkDocumentRequirements,
    handleDocumentConfirmation,
    shouldRenderFileUpload,
  };
};
